import { useState, useEffect, useCallback } from "react";
import { adminApi, type CouponRow } from "@/lib/superadmin";
import {
  PageHeader,
  Card,
  ErrorBanner,
  EmptyState,
  Skeleton,
  GhostButton,
  PrimaryButton,
} from "../components/ui";

function fmtDate(v?: string | null): string {
  if (!v) return "—";
  const d = new Date(v);
  if (isNaN(d.getTime())) return v;
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

const EMPTY: CouponRow = {
  code: "",
  description: "",
  discountType: "percent",
  discountValue: 10,
  appliesTo: "subscription",
  maxRedemptions: null,
  minAmount: null,
  expiresAt: null,
  active: true,
};

export default function CouponsPage() {
  const [rows, setRows] = useState<CouponRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState<CouponRow | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const r = await adminApi.coupons.list();
      setRows(r.rows);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load coupons.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const openNew = () => {
    setEditing({ ...EMPTY });
    setIsNew(true);
  };
  const openEdit = (c: CouponRow) => {
    setEditing({ ...c, expiresAt: c.expiresAt ? c.expiresAt.slice(0, 10) : null });
    setIsNew(false);
  };

  const save = async () => {
    if (!editing || !editing.code.trim()) return;
    setSaving(true);
    try {
      await adminApi.coupons.upsert({
        ...editing,
        code: editing.code.trim().toUpperCase(),
        expiresAt: editing.expiresAt ? new Date(editing.expiresAt).toISOString() : null,
      });
      setEditing(null);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (code: string) => {
    if (!confirm(`Delete coupon "${code}"?`)) return;
    try {
      await adminApi.coupons.delete(code);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Delete failed.");
    }
  };

  const fmtDiscount = (c: CouponRow) =>
    c.discountType === "percent" ? `${c.discountValue}% off` : `₹${c.discountValue} off`;

  return (
    <div className="p-4 md:p-6 space-y-5 max-w-[1400px] mx-auto">
      <PageHeader
        title="Coupons & Promotions"
        subtitle="Platform-wide promo codes for subscriptions and orders"
        actions={
          <>
            <GhostButton onClick={() => void load()}>
              <i className={`ri-refresh-line ${loading ? "animate-spin" : ""}`}></i>
              Refresh
            </GhostButton>
            <PrimaryButton onClick={openNew}>
              <i className="ri-add-line"></i>
              New coupon
            </PrimaryButton>
          </>
        }
      />

      <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-sky-50 border border-sky-100 text-sky-700 text-xs">
        <i className="ri-information-line text-sm shrink-0"></i>
        <span>
          Codes are validated at checkout via <code className="font-mono mx-1">ezydine.validate_coupon()</code>. Redemption counting is applied by the payment flow once wired.
        </span>
      </div>

      {error && <ErrorBanner message={error} onRetry={() => void load()} />}

      {loading ? (
        <Card className="space-y-3">
          {[0, 1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-11 w-full" />
          ))}
        </Card>
      ) : (
        <Card padded={false} className="overflow-hidden">
          {rows.length ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-foreground-500 border-b border-background-200 bg-background-100/50">
                    <th className="px-4 py-3 font-medium">Code</th>
                    <th className="px-4 py-3 font-medium">Discount</th>
                    <th className="px-4 py-3 font-medium">Scope</th>
                    <th className="px-4 py-3 font-medium">Usage</th>
                    <th className="px-4 py-3 font-medium">Expires</th>
                    <th className="px-4 py-3 font-medium text-center">Status</th>
                    <th className="px-4 py-3 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-background-100">
                  {rows.map((c) => (
                    <tr key={c.code} className="hover:bg-background-100/40">
                      <td className="px-4 py-3 font-mono font-semibold text-foreground-800">{c.code}</td>
                      <td className="px-4 py-3 text-foreground-700">{fmtDiscount(c)}</td>
                      <td className="px-4 py-3 text-foreground-600 capitalize">{c.appliesTo}</td>
                      <td className="px-4 py-3 text-foreground-600">
                        {c.redeemedCount ?? 0}
                        {c.maxRedemptions != null ? ` / ${c.maxRedemptions}` : " / ∞"}
                      </td>
                      <td className="px-4 py-3 text-foreground-600">{fmtDate(c.expiresAt)}</td>
                      <td className="px-4 py-3 text-center">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            c.active ? "bg-emerald-100 text-emerald-700" : "bg-background-200 text-foreground-500"
                          }`}
                        >
                          {c.active ? "Active" : "Off"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <button
                          onClick={() => openEdit(c)}
                          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-primary-600 hover:bg-primary-50 rounded-lg transition-colors cursor-pointer"
                        >
                          <i className="ri-pencil-line"></i>
                          Edit
                        </button>
                        <button
                          onClick={() => void remove(c.code)}
                          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-rose-600 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer ml-1"
                        >
                          <i className="ri-delete-bin-line"></i>
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState
              icon="ri-coupon-3-line"
              title="No coupons yet"
              hint="Create promo codes to drive signups and retention."
              action={
                <PrimaryButton onClick={openNew}>
                  <i className="ri-add-line"></i>
                  New coupon
                </PrimaryButton>
              }
            />
          )}
        </Card>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground-950/50 backdrop-blur-[2px] p-4" onClick={() => setEditing(null)}>
          <div className="bg-background-50 rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-heading font-bold text-foreground-900">{isNew ? "New coupon" : `Edit ${editing.code}`}</h2>
            <label className="text-sm block">
              <span className="text-foreground-600">Code</span>
              <input
                value={editing.code}
                disabled={!isNew}
                onChange={(e) => setEditing({ ...editing, code: e.target.value.toUpperCase() })}
                placeholder="WELCOME20"
                className="mt-1 w-full px-3 py-2 rounded-md border border-background-200 bg-background-50 font-mono disabled:opacity-60"
              />
            </label>
            <label className="text-sm block">
              <span className="text-foreground-600">Description</span>
              <input
                value={editing.description ?? ""}
                onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                className="mt-1 w-full px-3 py-2 rounded-md border border-background-200 bg-background-50"
              />
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="text-sm">
                <span className="text-foreground-600">Type</span>
                <select
                  value={editing.discountType}
                  onChange={(e) => setEditing({ ...editing, discountType: e.target.value as CouponRow["discountType"] })}
                  className="mt-1 w-full px-3 py-2 rounded-md border border-background-200 bg-background-50 cursor-pointer"
                >
                  <option value="percent">Percent %</option>
                  <option value="flat">Flat ₹</option>
                </select>
              </label>
              <label className="text-sm">
                <span className="text-foreground-600">Value</span>
                <input
                  type="number"
                  value={editing.discountValue}
                  onChange={(e) => setEditing({ ...editing, discountValue: Number(e.target.value) })}
                  className="mt-1 w-full px-3 py-2 rounded-md border border-background-200 bg-background-50"
                />
              </label>
              <label className="text-sm">
                <span className="text-foreground-600">Applies to</span>
                <select
                  value={editing.appliesTo}
                  onChange={(e) => setEditing({ ...editing, appliesTo: e.target.value as CouponRow["appliesTo"] })}
                  className="mt-1 w-full px-3 py-2 rounded-md border border-background-200 bg-background-50 cursor-pointer"
                >
                  <option value="subscription">Subscription</option>
                  <option value="order">Order</option>
                </select>
              </label>
              <label className="text-sm">
                <span className="text-foreground-600">Max redemptions</span>
                <input
                  type="number"
                  value={editing.maxRedemptions ?? ""}
                  placeholder="∞"
                  onChange={(e) => setEditing({ ...editing, maxRedemptions: e.target.value === "" ? null : Number(e.target.value) })}
                  className="mt-1 w-full px-3 py-2 rounded-md border border-background-200 bg-background-50"
                />
              </label>
              <label className="text-sm">
                <span className="text-foreground-600">Min amount (₹)</span>
                <input
                  type="number"
                  value={editing.minAmount ?? ""}
                  placeholder="0"
                  onChange={(e) => setEditing({ ...editing, minAmount: e.target.value === "" ? null : Number(e.target.value) })}
                  className="mt-1 w-full px-3 py-2 rounded-md border border-background-200 bg-background-50"
                />
              </label>
              <label className="text-sm">
                <span className="text-foreground-600">Expires</span>
                <input
                  type="date"
                  value={editing.expiresAt ?? ""}
                  onChange={(e) => setEditing({ ...editing, expiresAt: e.target.value || null })}
                  className="mt-1 w-full px-3 py-2 rounded-md border border-background-200 bg-background-50"
                />
              </label>
            </div>
            <label className="text-sm flex items-center gap-2">
              <input
                type="checkbox"
                checked={editing.active}
                onChange={(e) => setEditing({ ...editing, active: e.target.checked })}
                className="w-4 h-4 cursor-pointer"
              />
              <span className="text-foreground-600">Active</span>
            </label>
            <div className="flex items-center justify-end gap-2 pt-2">
              <button onClick={() => setEditing(null)} className="px-4 py-2 text-sm text-foreground-600 hover:bg-background-100 rounded-md cursor-pointer">Cancel</button>
              <button
                onClick={() => void save()}
                disabled={saving || !editing.code.trim()}
                className="px-4 py-2 text-sm font-semibold rounded-md bg-primary-600 text-background-50 hover:bg-primary-700 disabled:opacity-50 cursor-pointer"
              >
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
